import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { useQueryClient } from 'react-query'

import { useToast } from '@wartek-id/toast-v2'
import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'

import { assetUrl } from 'utils/url'
import {
  useAPIKinerja,
  useAPIUpdateState,
  apiPaths,
} from 'api/kinerja/useAPIKinerja'
import { PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { useGetKinerjaQuerykeys } from 'api/kinerja/utils/api'
import { PerencanaanPegawaiDetail } from 'app/KinerjaGpkPage/components/PerencanaanPegawaiDetail'
import { resolvePerencanaanData } from 'app/KinerjaGpkPage/utils/perencanaanData'
import { useGpkTracker } from 'app/KinerjaGpkPage/hooks/use-gpk-tracker'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { ResolvedPerencanaanData } from 'app/KinerjaGpkPage/types/perencanaan'

type PerencanaanPegawaiRangkumanProps = {
  onBack: () => void
}

export function PerencanaanPegawaiRangkuman({
  onBack,
}: PerencanaanPegawaiRangkumanProps) {
  const router = useRouter()
  const toast = useToast()
  const queryClient = useQueryClient()
  const { trackEvent } = useGpkTracker()
  const { periodYear } = usePeriodContext()
  const perencanaanQueryKey = useGetKinerjaQuerykeys(apiPaths.kinerja())

  // -----------------------------------------
  // Get perencanaan data
  // -----------------------------------------
  const { data: perencanaanData, isLoading: isPerencanaanLoading } =
    useAPIKinerja(
      {
        staleTime: 60 * 60 * 1000, // 1 hour
      },
      {
        tipe: 'GPK',
      }
    )

  const resolvedPerencanaanData: ResolvedPerencanaanData = useMemo(
    () => resolvePerencanaanData(perencanaanData?.data),
    [perencanaanData?.data]
  )

  // -----------------------------------------
  // Scroll to top on mount
  // -----------------------------------------
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  // -----------------------------------------
  // Handle events tracking
  // -----------------------------------------
  // Track submit action
  const handleTrackSubmitAction = () => {
    trackEvent({
      activityType: 'submit',
      screenName: 'perencanaan_pegawai',
      attributes: {
        year: String(periodYear),
      },
    })
  }

  // -----------------------------------------
  // Handle submission
  // -----------------------------------------
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false)
  const handleCloseSubmitDialog = () => {
    setIsSubmitDialogOpen(false)
  }

  const updateStateMutation = useAPIUpdateState()

  const handleSubmit = () => {
    updateStateMutation.mutate(
      {
        skpId: perencanaanData?.data?.id,
        tipe: 'GPK',
      },
      {
        onSuccess: () => {
          handleCloseSubmitDialog()

          toast({
            message: 'Berhasil mengajukan Perencanaan',
            type: 'success',
          })
          router.push(PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH)

          queryClient.invalidateQueries(perencanaanQueryKey)

          handleTrackSubmitAction()
        },
        onError: () => {
          handleCloseSubmitDialog()

          toast({
            message: 'Gagal mengajukan Perencanaan',
          })
        },
      }
    )
  }

  const isSubmitting = updateStateMutation.isLoading

  return (
    <>
      <div className="p-9">
        <div className="space-y-1 mb-9">
          <Text variant="heading-md">Rangkuman Perencanaan Kinerja Anda</Text>
          <ul className="list-disc list-outside pl-5">
            <Text as="li">
              Anda dapat cek ulang dan ubah sebelum mengajukan ke Tim Kinerja
              dan Pejabat Penilai Kinerja.
            </Text>
            <Text as="li">
              Format kalimat Perencanaan Kinerja sudah kami sesuaikan dengan
              aturan yang berlaku. Mohon dicek, ya.
            </Text>
          </ul>
        </div>

        <PerencanaanPegawaiDetail
          isLoading={isPerencanaanLoading}
          data={resolvedPerencanaanData}
        />
      </div>

      <div className="border-t border-subdued px-6 py-4 flex justify-end gap-5">
        <Button
          size="lg"
          color="white"
          onClick={onBack}
          disabled={isSubmitting}
        >
          Ubah
        </Button>
        <Button
          color="blue"
          size="lg"
          isLoading={isSubmitting}
          disabled={isPerencanaanLoading || isSubmitting}
          onClick={() => setIsSubmitDialogOpen(true)}
        >
          Ajukan Perencanaan
        </Button>
      </div>

      <Dialog
        isOpen={isSubmitDialogOpen}
        closeOnOverlayClick={false}
        size="sm"
        onClose={handleCloseSubmitDialog}
      >
        <DialogOverlay />
        <DialogContent
          aria-labelledby="confirmation-dialog-title"
          aria-describedby="confirmation-dialog-description"
        >
          <div className="p-6 space-y-4">
            <img
              src={assetUrl('/illustrations/img-publish-data.png')}
              alt=""
              width={84}
              height={84}
            />

            <Text variant="display-sm" as="h2" id="confirmation-dialog-title">
              Konfirmasi Ajukan Perencanaan
            </Text>

            <Text variant="body-lg" id="confirmation-dialog-description">
              Perencanaan tidak dapat diubah lagi dan akan diperiksa oleh Tim
              Kinerja dan Pejabat Penilai Kinerja.
            </Text>

            <div className="flex gap-5">
              <Button
                color="white"
                size="lg"
                className="w-full"
                disabled={isSubmitting}
                onClick={handleCloseSubmitDialog}
              >
                Kembali
              </Button>
              <Button
                color="blue"
                isLoading={isSubmitting}
                disabled={isSubmitting}
                size="lg"
                className="w-full"
                type="button"
                onClick={handleSubmit}
              >
                Konfirmasi
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
